import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NxMessageToastService } from '@aposin/ng-aquila/message';
import { SortDirection, SortEvent } from '@aposin/ng-aquila/table';
import { AppConfig } from 'src/config/app.config';
import { AuthService } from '../core/auth/auth.service';
import { NotificationService } from '../core/notification.service';
import { Subscription } from 'rxjs';
import * as moment from 'moment';

@Component({
  selector: 'app-dealer-commission-invoice',
  templateUrl: './dealer-commission-invoice.component.html',
  styleUrls: ['./dealer-commission-invoice.component.css']
})
export class DealerCommissionInvoiceComponent implements OnInit, OnDestroy {

  private _apiUrl: string;
  clientId: number;
  userTypeId: number;

  readonly months: string[] = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  currentYear: number = new Date().getFullYear();
  years: number[] = [this.currentYear - 1, this.currentYear];

  // ─── Commission Invoice ────────────────────────────────────────────────────

  count: number = 0;
  pageNumber: number = 1;
  pageSize: number = 10;

  invoiceList: any[] = [];
  searchTerm: string = '';
  statusFilter: string = 'All';
  startDate: moment.Moment | null = null;
  endDate: moment.Moment | null = null;
  exportData: any[] = [];
  fileName: string = '';

  uploadModalOpen: boolean = false;
  uploadForm!: FormGroup;
  uploadedFilePath: string | null = null;
  submitting: boolean = false;

  detailsModalOpen: boolean = false;
  selectedInvoice: any = null;

  acceptedTypes: string = 'application/pdf';
  private notifSub: Subscription = new Subscription();

  prevPage() { this.pageNumber--; this.getInvoiceList(); }
  nextPage() { this.pageNumber++; this.getInvoiceList(); }
  goToPage(n: number) { this.pageNumber = n; this.getInvoiceList(); }

  sortTable(sort: SortEvent) {
    this.invoiceList = this.invoiceList.sort((a, b) =>
      sort.active in a && sort.active in b ? this.compare(a[sort.active], b[sort.active], sort.direction) : 0
    );
  }

  // ─── Shared ────────────────────────────────────────────────────────────────

  compare(a: number | string | Date, b: number | string | Date, direction: SortDirection) {
    return (a < b ? -1 : 1) * (direction === 'asc' ? 1 : -1);
  }

  constructor(
    private authService: AuthService,
    private appConfig: AppConfig,
    private fb: FormBuilder,
    private messageToastService: NxMessageToastService,
    private notifService: NotificationService
  ) {
    this._apiUrl = appConfig.baseUrl;
    this.clientId = parseInt(atob(localStorage.getItem('CLIENT_ID') ?? ''));
    this.userTypeId = parseInt(atob(localStorage.getItem('USER_TYPE_ID') ?? ''));
  }

  ngOnInit(): void {
    this.uploadForm = this.fb.group({
      invoiceMonth: [null, Validators.required],
      invoiceYear: [this.currentYear, Validators.required],
      comments: ['', [Validators.required, Validators.maxLength(500)]]
    });

    this.getInvoiceList();
    this.notifService.pollNow();

    this.notifSub.add(
      this.notifService.newNotifications$.subscribe(() => this.getInvoiceList())
    );
  }

  ngOnDestroy(): void {
    this.notifSub.unsubscribe();
  }

  // ─── Commission methods ────────────────────────────────────────────────────
 // Test build
  getInvoiceList() {
    const statusParam = this.statusFilter === 'All' ? '' : this.statusFilter;
    const from = this.startDate ? this.startDate.format('LL') : '';
    const to = this.endDate ? this.endDate.format('LL') : '';
    this.authService
      .getList(
        this._apiUrl +
        `/api/v1/CommissionInvoice/list?pageNumber=${this.pageNumber}&pageSize=${this.pageSize}` +
        `&searchText=${this.searchTerm}&status=${statusParam}&startDate=${from}&endDate=${to}`
      )
      .subscribe(
        (response) => {
          if (response) {
            this.invoiceList = response.body || [];
            const xPagination = response.headers.get('X-Pagination');
            var pagingProp = xPagination ? JSON.parse(xPagination) : null;
            this.count = pagingProp ? pagingProp.TotalCount : 0;
            this.notifService.pollNow();
          }
        },
        (error) => console.error(error)
      );
  }

  exportToExcel() {
    const statusParam = this.statusFilter === 'All' ? '' : this.statusFilter;
    const from = this.startDate ? this.startDate.format('LL') : '';
    const to = this.endDate ? this.endDate.format('LL') : '';
    this.authService
      .getList(
        this._apiUrl +
        `/api/v1/CommissionInvoice/list?pageNumber=-1&pageSize=10000` +
        `&searchText=${this.searchTerm}&status=${statusParam}&startDate=${from}&endDate=${to}`
      )
      .subscribe(
        (response) => {
          if (response) {
            const rows = (response.body || []).map((item: any) => ({
              'Invoice Period': `${this.months[item.invoiceMonth - 1]} ${item.invoiceYear}`,
              'Submitted On': item.submittedOn ? new Date(item.submittedOn).toLocaleDateString('en-GB') : '',
              'Comments': item.comments || '',
              'Payment Date': item.paymentDate ? new Date(item.paymentDate).toLocaleDateString('en-GB') : '',
              'Payment Remarks': item.paymentRemarks || '',
              'Status': item.status || ''
            }));
            import('xlsx').then(xlsx => {
              const ws = xlsx.utils.json_to_sheet(rows);
              const wb = xlsx.utils.book_new();
              xlsx.utils.book_append_sheet(wb, ws, 'Commission Invoices');
              this.fileName = `CommissionInvoices_${new Date().toLocaleDateString('en-GB').replace(/\//g, '-')}.xlsx`;
              xlsx.writeFile(wb, this.fileName);
            });
          }
        },
        (error) => console.error(error)
      );
  }

  openUploadModal() {
    this.uploadForm.reset({ invoiceYear: this.currentYear });
    this.uploadedFilePath = null;
    this.uploadModalOpen = true;
  }

  closeUploadModal() {
    this.uploadModalOpen = false;
    this.uploadedFilePath = null;
  }

  onFileUploaded(event: any) {
    this.uploadedFilePath = event.dbPath;
  }

  submitInvoice() {
    this.uploadForm.markAllAsTouched();
    if (this.uploadForm.invalid) return;
    if (!this.uploadedFilePath) {
      this.messageToastService.open('Please upload a PDF invoice before submitting.', { context: 'info', duration: 4000 });
      return;
    }

    this.submitting = true;
    const payload = {
      dealerId: this.clientId,
      invoiceMonth: this.months.indexOf(this.uploadForm.value.invoiceMonth) + 1,
      invoiceYear: this.uploadForm.value.invoiceYear,
      comments: this.uploadForm.value.comments,
      filePath: this.uploadedFilePath
    };

    this.authService
      .post(this._apiUrl + '/api/v1/CommissionInvoice/submit', payload)
      .subscribe(
        () => {
          this.submitting = false;
          this.uploadModalOpen = false;
          this.messageToastService.open('Commission invoice submitted successfully.', { context: 'success', duration: 4000 });
          this.getInvoiceList();
        },
        (error) => {
          this.submitting = false;
          console.error(error);
          this.messageToastService.open('Submission failed. Please try again.', { context: 'info', duration: 4000 });
        }
      );
  }

  openDetailsModal(item: any) {
    this.selectedInvoice = item;
    this.detailsModalOpen = true;
    this.authService
      .get(this._apiUrl + `/api/v1/CommissionInvoice/details/${item.id}`)
      .subscribe(
        (response) => { if (response) this.selectedInvoice = response; },
        (error) => console.error(error)
      );
  }

  closeDetailsModal() {
    this.detailsModalOpen = false;
    this.selectedInvoice = null;
  }

  downloadInvoice(invoiceId: number, referenceNo: string) {
    this.authService
      .export(this._apiUrl + `/api/v1/CommissionInvoice/download/${invoiceId}`)
      .subscribe((data) => saveAs(data, `${referenceNo}_commission_invoice.pdf`));
  }

  badgeType(status: string): string {
    switch (status) {
      case 'Invoice Accepted':
      case 'Invoice Paid': return 'positive';
      case 'Invoice Rejected': return 'critical';
      case 'Submitted':
      case 'Invoice Under Process': return 'active';
      default: return 'active';
    }
  }

}
